import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  addClientContactsMutation,
  addClientNameMutation,
  cancelClientImportMutation,
  commitClientImportMutation,
  listClientsQueryKey,
  removeClientContactMutation,
  setClientDisplayNameMutation,
  unlinkClientMutation,
  updateClientContactLabelMutation,
  uploadClientImportMutation,
  type Client,
  type ClientContactRemoveResult,
  type ClientImportBatch,
} from '@/shared/api'
import { clientImportQuery, clientQuery } from './queries'

/**
 * Карточка и список — два ответа об одном госте, и устаревают они вместе: убрали канал — в строке
 * списка мог смениться основной телефон, добавили имя — сменился заголовок строки. Ключ списка без
 * аргументов совпадает по префиксу со всеми его вариантами (поиск, страницы): TanStack сопоставляет
 * объекты в ключе частично.
 */
function invalidateClient(queryClient: QueryClient, clientId: string) {
  void queryClient.invalidateQueries({ queryKey: clientQuery(clientId).queryKey })
  void queryClient.invalidateQueries({ queryKey: listClientsQueryKey() })
}

export type AddClientContactsRequest = {
  clientId: string
  contacts: ReadonlyArray<{ kind: string; value: string; label?: string }>
}

/**
 * Добавление каналов — БАТЧЕВОЕ и с ЧАСТИЧНЫМ УСПЕХОМ (§5): отказ по ОТДЕЛЬНОМУ каналу приезжает в
 * `outcomes` при успешном вызове, и читать его надо построчно. Отказ самого вызова (401, 404, обрыв
 * связи) при этом никуда не делся — он приходит обычной ошибкой мутации.
 *
 * ⚠️ Карточку всё равно перечитываем, хотя в исходе приходит `identityId` принятого канала. Собрать
 * из исхода строку нельзя: в нём нет ни подписи типа, ни атрибуции, ни признака «можно править» —
 * а всё это решает сервер. Дорисовать их самим значило бы завести вторую копию правила
 * принадлежности, то самое, которое намеренно оставлено серверу.
 */
export function useAddClientContacts() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...addClientContactsMutation(),
    onSuccess: (_result, variables) => invalidateClient(queryClient, variables.path.clientId),
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId, contacts }: AddClientContactsRequest) =>
      mutation.mutateAsync({ path: { clientId }, body: { contacts: [...contacts] } }),
  }
}

export type UpdateClientContactLabelRequest = {
  clientId: string
  identityId: string
  label: string
}

/** Метка — единственное, что у канала правится (§2.5). Значения канала операции правки нет вовсе. */
export function useUpdateClientContactLabel() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...updateClientContactLabelMutation(),
    onSuccess: (_result, variables) => invalidateClient(queryClient, variables.path.clientId),
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId, identityId, label }: UpdateClientContactLabelRequest) =>
      mutation.mutateAsync({ path: { clientId, identityId }, body: { label } }),
  }
}

export type RemoveClientContactRequest = { clientId: string; identityId: string }

/**
 * ⚠️ САМОЕ НЕОЧЕВИДНОЕ МЕСТО ДОМЕНА (§5). Удаление снимает претензию МОЕЙ компании, а не стирает
 * канал: строка в системе одна на всех. `channelRetired: false` означает «канал остался, его
 * заявляет кто-то ещё» — и строку из списка убирать НЕЛЬЗЯ.
 *
 * Уцелевший канал сервер возвращает целиком и в НОВОМ состоянии (у него сменились `editable` и
 * список источников) — этим и заменяем строку в кеше, ради чего поле и заведено. Считать новое
 * состояние на фронте значило бы держать вторую копию правила принадлежности.
 */
export function useRemoveClientContact() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...removeClientContactMutation(),
    onSuccess: (result: ClientContactRemoveResult, variables) => {
      const { clientId, identityId } = variables.path
      const survivor = result.channelRetired ? undefined : result.contact

      /*
       * ⚠️ «Канал уцелел, но самого канала не прислали» — единственный случай, когда МЫ НЕ ТРОГАЕМ
       * СПИСОК. Убрать строку здесь значило бы сделать ровно то, что §5 запрещает, а собрать её
       * новое состояние самим нельзя: принадлежность считает сервер. Молча ждём перечитывания.
       */
      if (result.channelRetired || survivor) {
        queryClient.setQueryData(clientQuery(clientId).queryKey, (previous: Client | undefined) => {
          if (!previous) return previous
          return {
            ...previous,
            contacts: survivor
              ? previous.contacts.map((contact) =>
                  contact.identityId === identityId ? survivor : contact,
                )
              : previous.contacts.filter((contact) => contact.identityId !== identityId),
          }
        })
      }

      // Патч выше — ради мгновенного отклика, инвалидация — ради правды: фоновый перезапрос
      // (`refetchOnWindowFocus`) мог бы приехать уже после патча и вернуть убранную строку.
      invalidateClient(queryClient, clientId)
    },
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId, identityId }: RemoveClientContactRequest) =>
      mutation.mutateAsync({ path: { clientId, identityId } }),
  }
}

export type AddClientNameRequest = { clientId: string; raw: string }

/**
 * ⚠️ ИМЯ НЕ ЗАМЕНЯЕТСЯ, А ДОПОЛНЯЕТСЯ (§2.4): из брони пришло «Иван», менеджер узнал «Петров Иван
 * Сергеевич» — в карточке будет ДВА варианта, а не замена. Так компания видит и старую выгрузку, и
 * полное ФИО из разговора, и понимает, что это один человек. Удаления варианта нет вовсе (§11).
 *
 * Ответ — гость целиком, им и обновляем карточку: заново её читать незачем.
 */
export function useAddClientName() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...addClientNameMutation(),
    onSuccess: (client: Client, variables) => {
      // Ключ берём из ЗАПРОСА, а не из ответа: равенство `client.id` и запрошенного id ничем не
      // обещано, а в этом домене есть слияние карточек (`origin: MERGE`) — то есть ответ вправе
      // приехать про другую запись, и патч ушёл бы мимо открытой карточки.
      queryClient.setQueryData(clientQuery(variables.path.clientId).queryKey, client)
      void queryClient.invalidateQueries({ queryKey: listClientsQueryKey() })
    },
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId, raw }: AddClientNameRequest) =>
      mutation.mutateAsync({ path: { clientId }, body: { raw } }),
  }
}

export type SetClientDisplayNameRequest = { clientId: string; nameId: string }

/** Основной вариант виден в списках и на кнопках у гостя — поэтому список тоже устаревает. */
export function useSetClientDisplayName() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...setClientDisplayNameMutation(),
    onSuccess: (client: Client, variables) => {
      // Ключ берём из ЗАПРОСА, а не из ответа: равенство `client.id` и запрошенного id ничем не
      // обещано, а в этом домене есть слияние карточек (`origin: MERGE`) — то есть ответ вправе
      // приехать про другую запись, и патч ушёл бы мимо открытой карточки.
      queryClient.setQueryData(clientQuery(variables.path.clientId).queryKey, client)
      void queryClient.invalidateQueries({ queryKey: listClientsQueryKey() })
    },
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId, nameId }: SetClientDisplayNameRequest) =>
      mutation.mutateAsync({ path: { clientId }, body: { nameId } }),
  }
}

/**
 * ⚠️ ЭТО НЕ УДАЛЕНИЕ ГОСТЯ (§5). Снимается связь с МОЕЙ компанией: гость пропадает из моего списка,
 * но остаётся в базах других компаний и вернётся сам, если снова забронирует. Формулировка в
 * интерфейсе — «Убрать из базы», и никогда «Удалить».
 */
export function useUnlinkClient() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...unlinkClientMutation(),
    onSuccess: (_result, variables) => invalidateClient(queryClient, variables.path.clientId),
  })

  return {
    ...mutation,
    mutateAsync: ({ clientId }: { clientId: string }) =>
      mutation.mutateAsync({ path: { clientId } }),
  }
}

/*
 * ⚠️ У ВСЕХ ТРЁХ ОПЕРАЦИЙ ЗАГРУЗКИ СТОИТ `meta.handledInPlace`. Их отказы экран показывает САМ и на
 * месте: у разбора файла спека прямо требует показать `Problem.detail` как есть (§10) — только
 * сервер знает, что это `.xls`, а не `.xlsx`, — а у применения 409 означает «судьба уже решена» и
 * сопровождается готовым заголовком. Без пометки поверх точного объяснения встал бы общий тост
 * «Что-то сломалось, обновите страницу»: `classifyError` не отличает такой отказ от бага приложения.
 */
// Совет при неизвестном исходе — свой: списка загрузок нет, проверять негде, а повтор безопасен.
const importHandled = (subject: string, unknownOutcome: string) =>
  ({ handledInPlace: true, subject, unknownOutcome }) as const

/**
 * Загрузка файла базы — ФАЗА 1. Отвечает отчётом и НЕ ПИШЕТ НИЧЕГО: гости появятся только после
 * применения. Поэтому здесь нечего инвалидировать — база гостей от разбора файла не меняется.
 *
 * Отчёт кладём в кеш сразу: экран уходит на `?import=<id>` и читает состояние оттуда, а без записи
 * он показал бы скелет поверх уже полученных данных.
 */
export function useUploadClientImport() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...uploadClientImportMutation(),
    meta: importHandled('Разбор файла базы гостей', 'залейте файл ещё раз: повтор ничего не задвоит.'),
    onSuccess: (batch: ClientImportBatch) => {
      queryClient.setQueryData(clientImportQuery(batch.id).queryKey, batch)
    },
  })

  return {
    ...mutation,
    mutateAsync: (file: File) => mutation.mutateAsync({ body: { file } }),
  }
}

/**
 * Применение — ФАЗА 2. Вызов возвращается сразу, применение идёт фоном, а ход менеджеру показывает
 * опрос состояния (`clientImportQuery`). Ответ — самое свежее, что у нас есть, им и закрываем
 * состояние; ждём же его затем, чтобы поймать отказ.
 *
 * База гостей после применения устаревает НАСТОЯЩИМ образом: в ней появились тысячи строк. Список
 * инвалидируем по префиксу — он накрывает и поиск, и все загруженные страницы, и счётчик базы.
 */
export function useCommitClientImport() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...commitClientImportMutation(),
    meta: importHandled('Загрузка базы гостей', 'проверьте базу гостей; повтор ничего не задвоит.'),
    /*
     * Оптимистичная запись переводит кеш в `committing` ДО ответа, и этим делает две вещи: убирает
     * кадр с кнопкой «Применить» над уже применяемой загрузкой и запускает опрос — интервал
     * считается по `status` в кеше (`clientImportQuery`).
     *
     * ⚠️ ЕДИНСТВЕННЫМ ПУСКОВЫМ МЕХАНИЗМОМ ОНА БЫТЬ ПЕРЕСТАЛА, а была им: пока сервис отвечал по
     * завершении, перевести кеш в `committing` было больше нечем, и без этой записи экран стоял бы
     * пустым всю минуту. Теперь `commitClientImport` возвращается сразу и уже с `committing` — то
     * есть опрос начался бы и с ответа, просто позже.
     *
     * Гонки с опросом здесь нет: на момент записи он ещё выключен, включает его она сама.
     */
    onMutate: (variables) => {
      queryClient.setQueryData(
        clientImportQuery(variables.path.importId).queryKey,
        (previous: ClientImportBatch | undefined) =>
          // ⚠️ Причину прошлого сбоя СНИМАЕМ вместе со статусом: она означает «не идёт», и,
          // уцелев в спреде, остановила бы опрос сразу после старта — то есть ровно тот опрос,
          // ради запуска которого эта запись и делается.
          previous ? { ...previous, status: 'committing' as const, error: undefined } : previous,
      )
    },
    onSuccess: async (batch: ClientImportBatch, variables) => {
      const key = clientImportQuery(variables.path.importId).queryKey
      // ⚠️ Опрос К ЭТОМУ МОМЕНТУ ИДЁТ, и его ответ, пришедший после ручной записи, откатил бы
      // счётчики назад (записанное правило: «поллинг съедает то, что положили в кеш руками»).
      // Отменять надо ЗДЕСЬ, перед записью, — в `onMutate` отмена не помогает: запрос стартует позже.
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData(key, batch)
      void queryClient.invalidateQueries({ queryKey: listClientsQueryKey() })
    },
    onError: (_error, variables) => {
      // 409 — «судьба уже решена»: наше представление отстало от сервера, и правду надо перечитать.
      // Заодно это снимает оптимистичный `committing` выше, если применения на самом деле не было.
      void queryClient.invalidateQueries({
        queryKey: clientImportQuery(variables.path.importId).queryKey,
      })
    },
  })

  return {
    ...mutation,
    mutateAsync: ({ importId }: { importId: string }) =>
      mutation.mutateAsync({ path: { importId } }),
  }
}

/** Отказ от разобранного файла. Записи не было, поэтому база гостей не устаревает. */
export function useCancelClientImport() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    ...cancelClientImportMutation(),
    meta: importHandled(
      'Отмена загрузки базы гостей',
      'повтор безопасен: если судьба загрузки уже решена, сервер так и скажет.',
    ),
    onSuccess: (batch: ClientImportBatch, variables) => {
      queryClient.setQueryData(clientImportQuery(variables.path.importId).queryKey, batch)
    },
    onError: (_error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: clientImportQuery(variables.path.importId).queryKey,
      })
    },
  })

  return {
    ...mutation,
    mutateAsync: ({ importId }: { importId: string }) =>
      mutation.mutateAsync({ path: { importId } }),
  }
}
